import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Mic, Upload, FileAudio, CheckCircle, Play, Pause, FileText, Users as UsersIcon, List, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import './styles/VibeMeeting.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VibeMeeting({ user }) {
  const [meetings, setMeetings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newMeetingTitle, setNewMeetingTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [approvedTasks, setApprovedTasks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [currentRecordingMeetingId, setCurrentRecordingMeetingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchMeetings();
      fetchTeamMembers();
    }
  }, [selectedTeam]);

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams/${selectedTeam}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data.filter(m => !m.is_pending));
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      if (response.data.length > 0) {
        setSelectedTeam(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch teams');
    }
  };

  const fetchMeetings = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/meetings?team_id=${selectedTeam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(response.data);
    } catch (error) {
      toast.error('Failed to fetch meetings');
    }
  };

  const createMeeting = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/meetings`, {
        title: newMeetingTitle,
        team_id: selectedTeam
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meeting created');
      setIsDialogOpen(false);
      setNewMeetingTitle('');
      fetchMeetings();
    } catch (error) {
      toast.error('Failed to create meeting');
    }
  };

  const MAX_AUDIO_BYTES = 25 * 1024 * 1024;
  const [uploadProgress, setUploadProgress] = useState(null); // { meetingId, percent }
  const [confirmDelete, setConfirmDelete] = useState(null);

  const uploadBlob = async (meetingId, blob, filename) => {
    if (blob.size > MAX_AUDIO_BYTES) { toast.error('Recording is larger than 25 MB. Trim it or use a compressed format (mp3, m4a).'); return false; }
    if (!blob.size) { toast.error('That recording is empty'); return false; }
    setProcessing(true);
    setUploadProgress({ meetingId, percent: 0 });
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', blob, filename);
      await axios.post(`${API}/meetings/${meetingId}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (e) => setUploadProgress({ meetingId, percent: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }),
      });
      setUploadProgress({ meetingId, percent: 100 });
      toast.success('Audio uploaded, transcribing…');
      await transcribeMeeting(meetingId);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || (error.code === 'ERR_NETWORK' ? 'Upload interrupted — check your connection and try again' : 'Failed to upload audio'));
      return false;
    } finally {
      setProcessing(false);
      setUploadProgress(null);
    }
  };

  const uploadAudio = async (meetingId) => {
    if (!selectedFile) return;
    if (await uploadBlob(meetingId, selectedFile, selectedFile.name)) setSelectedFile(null);
  };

  const transcribeMeeting = async (meetingId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/meetings/${meetingId}/transcribe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transcription complete, analyzing...');
      await analyzeMeeting(meetingId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transcribe meeting');
      fetchMeetings();
    }
  };

  const analyzeMeeting = async (meetingId) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/meetings/${meetingId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Analysis complete!');
      fetchMeetings();
      
      const meeting = meetings.find(m => m.id === meetingId);
      if (meeting) {
        setSelectedMeeting({ ...meeting, ...response.data });
      }
    } catch (error) {
      toast.error('Failed to analyze meeting');
    }
  };

  const reviewAndCreateTasks = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const tasksToCreate = selectedMeeting.tasks_identified.filter((_, index) => 
        approvedTasks.includes(index)
      );
      
      await axios.post(`${API}/meetings/${selectedMeeting.id}/review`, 
        { approved_tasks: tasksToCreate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${tasksToCreate.length} task(s) created successfully!`);
      setSelectedMeeting(null);
      setApprovedTasks([]);
      fetchMeetings();
      
      // Notify other components
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
    } catch (error) {
      toast.error('Failed to create tasks');
    }
  };


  const deleteMeeting = async (meetingId) => {
    setConfirmDelete(null);
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/meetings/${meetingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meeting deleted successfully');
      fetchMeetings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete meeting');
    }
  };


  const toggleAudioPlayback = async (meetingId) => {
    const audioElement = document.getElementById(`audio-${meetingId}`);
    if (audioElement) {
      if (isPlaying[meetingId]) {
        audioElement.pause();
        setIsPlaying({ ...isPlaying, [meetingId]: false });
      } else {
        // If audio source is not loaded yet, fetch it with auth headers
        if (!audioElement.src || audioElement.src.includes('/audio')) {
          try {
            const token = localStorage.getItem('session_token');
            console.log('Fetching audio for meeting:', meetingId);
            const response = await axios.get(`${API}/meetings/${meetingId}/audio`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            });
            const audioBlob = response.data;
            console.log('Audio blob received:', audioBlob.size, 'bytes, type:', audioBlob.type);
            const audioUrl = URL.createObjectURL(audioBlob);
            audioElement.src = audioUrl;
            console.log('Audio source set to:', audioUrl);
          } catch (error) {
            console.error('Audio load error:', error);
            toast.error('Failed to load audio');
            return;
          }
        }
        
        try {
          await audioElement.play();
          setIsPlaying({ ...isPlaying, [meetingId]: true });
          console.log('Audio playback started');
        } catch (playError) {
          console.error('Playback error:', playError);
          toast.error('Failed to play audio');
        }
      }
    }
  };

  const getMemberName = (memberId) => {
    const member = teamMembers.find(m => m.id === memberId);
    return member ? member.name : 'Unassigned';
  };

  const startRecording = async (meetingId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        await uploadRecordedAudio(meetingId, blob);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setRecordedChunks(chunks);
      setIsRecording(true);
      setCurrentRecordingMeetingId(meetingId);
      toast.success('Recording started');
    } catch (error) {
      toast.error('Failed to access microphone. Please grant permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setCurrentRecordingMeetingId(null);
      toast.success('Recording stopped, processing...');
    }
  };

  const uploadRecordedAudio = async (meetingId, audioBlob) => {
    await uploadBlob(meetingId, audioBlob, 'recording.webm');
  };

  return (
    <div className="vibe-meeting" data-testid="vibe-meeting">
      <div className="page-header">
        <div>
          <h1 className="page-title">VibeMeeting</h1>
          <p className="page-subtitle">Record, transcribe, and auto-assign tasks from meetings</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-[200px]" data-testid="team-selector">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent>
              {teams.map(team => (
                <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-meeting-button">
                <Mic size={20} style={{ marginRight: '0.5rem' }} />
                New Meeting
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-meeting-dialog">
              <DialogHeader>
                <DialogTitle>Create New Meeting</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Meeting title"
                  value={newMeetingTitle}
                  onChange={(e) => setNewMeetingTitle(e.target.value)}
                  data-testid="meeting-title-input"
                />
                <Button onClick={createMeeting} data-testid="create-meeting-submit">Create Meeting</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="meetings-grid">
        {meetings.map(meeting => (
          <Card key={meeting.id} className="meeting-card" data-testid={`meeting-card-${meeting.id}`}>
            <div className="meeting-header">
              <div>
                <h3>{meeting.title}</h3>
                <p className="meeting-date">{new Date(meeting.created_at).toLocaleString()}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {meeting.reviewed && (
                  <Badge variant="success" style={{ background: '#10b981', color: 'white' }}>
                    <CheckCircle size={16} style={{ marginRight: '0.25rem' }} />
                    Reviewed
                  </Badge>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setConfirmDelete(meeting.id)}
                  style={{ color: '#ef4444' }}
                  title="Delete meeting"
                  data-testid={`delete-meeting-${meeting.id}`}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
            
            {/* Audio Player - Show for all meetings with transcription (which means audio was uploaded) */}
            {(meeting.has_audio || meeting.transcription) && (
              <div className="audio-player-section" style={{ marginTop: '1rem' }}>
                <audio 
                  id={`audio-${meeting.id}`}
                  onEnded={() => setIsPlaying({ ...isPlaying, [meeting.id]: false })}
                  onError={(e) => {
                    console.error('Audio playback error:', e);
                    toast.error('Audio not available');
                    setIsPlaying({ ...isPlaying, [meeting.id]: false });
                  }}
                  style={{ display: 'none' }}
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => toggleAudioPlayback(meeting.id)}
                  data-testid={`play-audio-${meeting.id}`}
                >
                  {isPlaying[meeting.id] ? <Pause size={16} /> : <Play size={16} />}
                  <span style={{ marginLeft: '0.5rem' }}>
                    {isPlaying[meeting.id] ? 'Pause' : 'Play'} Recording
                  </span>
                </Button>
              </div>
            )}
            
            {!meeting.transcription && (
              <div className="upload-section">
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                  {isRecording && currentRecordingMeetingId === meeting.id ? (
                    <Button 
                      onClick={stopRecording} 
                      variant="destructive"
                      disabled={processing}
                      data-testid={`stop-recording-${meeting.id}`}
                    >
                      <Mic size={20} style={{ marginRight: '0.5rem' }} />
                      Stop Recording
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => startRecording(meeting.id)} 
                      disabled={processing || isRecording}
                      data-testid={`start-recording-${meeting.id}`}
                    >
                      <Mic size={20} style={{ marginRight: '0.5rem' }} />
                      Start Recording
                    </Button>
                  )}
                  
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    style={{ display: 'none' }}
                    id={`file-${meeting.id}`}
                    data-testid={`file-input-${meeting.id}`}
                  />
                  <Button 
                    variant="outline" 
                    disabled={processing || isRecording}
                    onClick={() => document.getElementById(`file-${meeting.id}`).click()}
                    data-testid={`choose-audio-${meeting.id}`}
                  >
                    <Upload size={20} style={{ marginRight: '0.5rem' }} />
                    Choose Audio
                  </Button>
                  {selectedFile && (
                    <Button onClick={() => uploadAudio(meeting.id)} disabled={processing || isRecording} data-testid={`upload-button-${meeting.id}`}>
                      <FileAudio size={20} style={{ marginRight: '0.5rem' }} />
                      {processing ? 'Processing...' : `Upload & Process (${(selectedFile.size / 1024 / 1024).toFixed(1)} MB)`}
                    </Button>
                  )}
                </div>
                {uploadProgress?.meetingId === meeting.id && (
                  <div className="upload-progress" data-testid={`upload-progress-${meeting.id}`}>
                    <div className="upload-progress-bar"><span style={{ width: `${uploadProgress.percent}%` }} /></div>
                    <span>{uploadProgress.percent < 100 ? `Uploading… ${uploadProgress.percent}%` : 'Uploaded — transcribing with Whisper…'}</span>
                  </div>
                )}
                <p className="upload-hint">mp3, wav, m4a, ogg, webm or flac · up to 25 MB</p>
              </div>
            )}
            
            {meeting.transcription && (
              <div className="meeting-info" style={{ marginTop: '1rem' }}>
                <Badge variant="outline">
                  <FileText size={14} style={{ marginRight: '0.25rem' }} />
                  Transcribed
                </Badge>
              </div>
            )}
            
            {meeting.summary && (
              <div className="meeting-summary" style={{ marginTop: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FileText size={18} />
                  Summary
                </h4>
                <p style={{ color: '#64748b', marginBottom: '1rem' }}>{meeting.summary}</p>
                {meeting.tasks_identified && meeting.tasks_identified.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <Badge variant="secondary">
                      <List size={14} style={{ marginRight: '0.25rem' }} />
                      {meeting.tasks_identified.length} Task(s) Identified
                    </Badge>
                  </div>
                )}
                <Button 
                  onClick={() => setSelectedMeeting(meeting)} 
                  size="sm"
                  disabled={meeting.reviewed}
                  data-testid={`review-button-${meeting.id}`}
                >
                  {meeting.reviewed ? 'Tasks Already Created' : 'Review & Create Tasks'}
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete this meeting?"
        description="The recording, transcript and summary will be removed. Tasks already created from it are not affected."
        onConfirm={() => deleteMeeting(confirmDelete)}
        testId="delete-meeting-confirm"
      />

      {selectedMeeting && (
        <Dialog open={!!selectedMeeting} onOpenChange={() => setSelectedMeeting(null)}>
          <DialogContent className="max-w-4xl" data-testid="review-dialog" style={{ maxHeight: '90vh', overflow: 'auto' }}>
            <DialogHeader>
              <DialogTitle style={{ fontSize: '1.5rem' }}>
                Review Meeting: {selectedMeeting.title}
              </DialogTitle>
              <p style={{ color: '#64748b', marginTop: '0.5rem' }}>
                {new Date(selectedMeeting.created_at).toLocaleString()}
              </p>
            </DialogHeader>
            <div className="review-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Meeting Summary */}
              <div className="summary-section" style={{ 
                background: 'linear-gradient(135deg, rgba(26, 147, 111, 0.08) 0%, rgba(15, 107, 79, 0.08) 100%)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #d1f4e0'
              }}>
                <h4 style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  <FileText size={20} />
                  Meeting Minutes
                </h4>
                <p style={{ 
                  lineHeight: '1.6',
                  color: '#475569'
                }}>{selectedMeeting.summary}</p>
              </div>
              
              {/* Transcription (if available) */}
              {selectedMeeting.transcription && (
                <details style={{ 
                  background: '#f8fafc',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <summary style={{ 
                    cursor: 'pointer',
                    fontWeight: '600',
                    marginBottom: '0.5rem'
                  }}>
                    View Full Transcription
                  </summary>
                  <p style={{ 
                    marginTop: '1rem',
                    lineHeight: '1.6',
                    color: '#64748b',
                    maxHeight: '200px',
                    overflow: 'auto'
                  }}>
                    {selectedMeeting.transcription}
                  </p>
                </details>
              )}
              
              {/* Tasks Section */}
              <div className="tasks-section">
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                  }}>
                    <List size={20} />
                    Action Items ({selectedMeeting.tasks_identified?.length || 0})
                  </h4>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setApprovedTasks(selectedMeeting.tasks_identified?.map((_, i) => i) || [])}
                    >
                      Select All
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setApprovedTasks([])}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {selectedMeeting.tasks_identified?.map((task, index) => (
                    <Card 
                      key={index} 
                      className="task-review-item"
                      style={{
                        padding: '1.25rem',
                        border: approvedTasks.includes(index) ? '2px solid #1a936f' : '1px solid #e2e8f0',
                        background: approvedTasks.includes(index) ? 'rgba(26, 147, 111, 0.08)' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => {
                        if (approvedTasks.includes(index)) {
                          setApprovedTasks(approvedTasks.filter(i => i !== index));
                        } else {
                          setApprovedTasks([...approvedTasks, index]);
                        }
                      }}
                    >
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'start' }}>
                        <Checkbox
                          checked={approvedTasks.includes(index)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setApprovedTasks([...approvedTasks, index]);
                            } else {
                              setApprovedTasks(approvedTasks.filter(i => i !== index));
                            }
                          }}
                          data-testid={`task-checkbox-${index}`}
                        />
                        <div style={{ flex: 1 }}>
                          <h5 style={{ 
                            fontWeight: '600',
                            marginBottom: '0.5rem',
                            fontSize: '1rem'
                          }}>{task.title}</h5>
                          <p style={{ 
                            color: '#64748b',
                            marginBottom: '0.75rem',
                            lineHeight: '1.5'
                          }}>{task.description}</p>
                          {task.assigned_to && (
                            <Badge variant="secondary" style={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <UsersIcon size={14} />
                              {getMemberName(task.assigned_to)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e2e8f0'
              }}>
                <Button 
                  onClick={reviewAndCreateTasks} 
                  disabled={approvedTasks.length === 0}
                  data-testid="approve-tasks-button"
                  style={{ flex: 1 }}
                >
                  <CheckCircle size={18} style={{ marginRight: '0.5rem' }} />
                  Create {approvedTasks.length} Task(s)
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedMeeting(null);
                    setApprovedTasks([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
